/**
 * Navigation Configuration for Empresa Customers
 * Specific navigation menu for corporate/business clients
 * Includes factories, offices, retail, and industrial facilities
 */

import { NavGroup } from './types';
import { RoleName } from '../roles';

export function getEmpresaNavigation(customer?: any): NavGroup[] {
  const guaritaEnabled = !!(customer?.empresa_data?.guarita_integration_enabled || customer?.condo_data?.guarita_integration_enabled);
  const alphadigiEnabled = !!(customer?.empresa_data?.alphadigi_lpr_enabled || customer?.condo_data?.alphadigi_lpr_enabled);
  // See condominio.ts — the Hikvision LPR toggle must also reveal Integrações.
  const hikvisionLprEnabled = !!(customer?.empresa_data?.hikvision_lpr_enabled || customer?.condo_data?.hikvision_lpr_enabled);
  const timeZonesEnabled = !!(customer?.empresa_data?.time_zones_enabled || customer?.condo_data?.time_zones_enabled);
  const keysEnabled = !!(customer?.empresa_data?.keys_enabled);
  const muralAnunciosEnabled = !!(customer?.empresa_data?.mural_anuncios_enabled);
  const livroEnabled = !!(customer?.empresa_data?.livro_enabled);
  const lostFoundEnabled = !!(customer?.empresa_data?.lost_found_enabled);
  // Defaults ON — see the condomínio navigation for why this one flag is not `!!`.
  const solicitacoesEnabled = customer?.empresa_data?.solicitacoes_enabled !== false;
  return [
  {
    group: 'Visão Geral',
    icon: 'LayoutDashboard',
    minRoleLevel: 70,
    items: [
      { name: 'Empresa', href: '/dashboard', icon: 'Home' },
    ],
  },
  {
    group: 'Cadastro',
    icon: 'FolderOpen',
    items: [
      {
        name: 'Funcionários',
        href: '/employees',
        icon: 'Briefcase',
      },
      {
        name: 'Visitantes',
        href: '/visitors',
        icon: 'ClipboardCheck',
      },
      {
        name: 'Prestadores',
        href: '/prestadores',
        icon: 'Briefcase',
      },
      {
        // Every clearance in the empresa — see the condomínio navigation for the rationale.
        name: 'Liberados',
        href: '/liberados',
        icon: 'CalendarCheck',
        minRoleLevel: 60,
      },
      ...(solicitacoesEnabled ? [{
        // See the condomínio navigation: the staff side of the app's credential requests.
        name: 'Solicitações',
        href: '/solicitacoes',
        icon: 'ClipboardCheck',
        minRoleLevel: 60,
      }] : []),
      {
        name: 'Pessoas',
        href: '/people',
        icon: 'Users',
      },
      {
        name: 'Veículos',
        href: '/vehicles',
        icon: 'Car',
      },
      {
        name: 'Encomendas',
        href: '/encomendas',
        icon: 'Package',
      },
      {
        name: 'Tags de Acesso',
        href: '/tags',
        icon: 'Tag',
        minRoleLevel: 60, // Staff and above
      },
      {
        name: 'Qualidade de Fotos',
        href: '/photo-quality',
        icon: 'ScanFace',
        minRoleLevel: 60,
        electronOnly: true,
      },
    ],
  },
  {
    group: 'Estrutura',
    icon: 'Network',
    items: [
      {
        name: 'Departamentos',
        href: '/departments',
        icon: 'Building',
      },
      ...(keysEnabled ? [{
        name: 'Chaves',
        href: '/keys',
        icon: 'Key',
        minRoleLevel: 60,
      }] : []),
    ],
  },
  ...((muralAnunciosEnabled || livroEnabled || lostFoundEnabled) ? [{
    group: 'Comunicação',
    icon: 'Megaphone',
    items: [
      ...(muralAnunciosEnabled ? [{
        name: 'Mural de Anúncios',
        href: '/mural-anuncios',
        icon: 'Megaphone',
      }] : []),
      // Same livro_enabled flag the Operational View's button already honours.
      ...(livroEnabled ? [{
        name: 'Livro de Ocorrências',
        href: '/livro',
        icon: 'BookOpen',
      }] : []),
      // Same lost_found_enabled flag the apps' tiles honour, so the feature appears and
      // disappears together everywhere.
      ...(lostFoundEnabled ? [{
        name: 'Achados e Perdidos',
        href: '/achados-perdidos',
        icon: 'Package',
      }] : []),
    ],
  }] : []),
  {
    group: 'Acesso',
    icon: 'ShieldAlert',
    minRoleLevel: 60, // Staff and above
    items: [
      {
        name: 'Monitoramento',
        href: '/access-control',
        icon: 'Eye',
        // No minRoleLevel - visible to all staff/managers (administrador 65, sindico 70, porteiro 60, etc.)
      },
      {
        name: 'Dispositivos de Pessoas',
        href: '/person-devices',
        icon: 'CreditCard',
      },
      {
        name: 'Eventos Gerais',
        href: '/eventos-gerais',
        icon: 'Activity',
        excludeRoles: ['sindico'],
      },
      {
        name: 'Zonas & Dispositivos',
        href: '/zones-devices',
        icon: 'Rss',
        minRoleLevel: 70, // Sindico and above - not visible to administrador (65) or porteiro (60)
      },
      {
        name: 'Grupos de Zonas',
        href: '/zone-groups',
        icon: 'FolderTree',
        minRoleLevel: 80, // Admin only - hidden from sindico
      },
      {
        name: 'Perfis de Acesso',
        href: '/access-profiles',
        icon: 'Lock',
        minRoleLevel: 80, // Admin only - hidden from sindico
      },
      ...(timeZonesEnabled ? [{
        name: 'Zonas de Tempo',
        href: '/time-zones',
        icon: 'Clock',
        minRoleLevel: 80, // Admin only
      }] : []),
      {
        name: 'Câmeras de Cadastro',
        href: '/registration-cameras',
        icon: 'ScanLine',
        minRoleLevel: 80, // Admin only - hidden from sindico
      },
      {
        name: 'Eventos de Dispositivos',
        href: '/device-audit',
        icon: 'Activity',
        minRoleLevel: 70,
      },
      {
        name: 'Reconciliação de Acesso',
        href: '/reconciliation',
        icon: 'AlertTriangle',
        minRoleLevel: 70, // Síndico and above — matches backend's manual-resolve role gate
      },
      {
        name: 'Auditoria de Cadastros',
        href: '/role-audit',
        icon: 'ClipboardList',
        minRoleLevel: 70,
        excludeRoles: ['sindico'],
      },
    ],
  },
  {
    group: 'Avançado',
    icon: 'Wrench',
    items: [
      {
        name: 'Gerenciar QR Codes',
        href: '/qr-codes',
        icon: 'QrCode',
        requiredRoles: ['super_admin', 'admin'], // Admin only
      },
      {
        name: 'Importar Dados',
        href: '/import-data',
        icon: 'FileUp',
        excludeRoles: ['sindico'],
      },
      {
        name: 'Exportar Dados',
        href: '/export-data',
        icon: 'FileDown',
        excludeRoles: ['sindico'],
      },
      {
        name: 'Pessoas Não Autorizadas',
        href: '/admin/unauthorized-persons',
        icon: 'AlertTriangle',
        requiredRoles: ['super_admin', 'admin'], // Admin only diagnostic tool
        excludeRoles: ['sindico'],
      },
      ...((guaritaEnabled || alphadigiEnabled || hikvisionLprEnabled) ? [{
        name: 'Integrações',
        href: '/integrations',
        icon: 'Plug',
        requiredRoles: ['super_admin', 'admin'] as RoleName[],
      }] : []),
    ],
    minRoleLevel: 70, // Manager (Administrador) and above
  },
  {
    group: 'Administração',
    icon: 'Settings',
    items: [
      {
        name: 'Usuários',
        href: '/customer-admin',
        icon: 'UserCog',
      },
      {
        name: 'Configurações do Cliente',
        href: '/customer-settings',
        icon: 'Building2',
        requiredRoles: ['super_admin', 'admin'], // Only super_admin and admin
      },
    ],
    minRoleLevel: 70, // Manager (Administrador) and above
  },
  {
    group: 'Ajuda',
    icon: 'HelpCircle',
    items: [
      {
        name: 'Central de Ajuda',
        href: '/support',
        icon: 'Book',
      },
      {
        name: 'Downloads',
        href: '/downloads',
        icon: 'Download',
        minRoleLevel: 70,
      },
    ],
  },
  ];
}

// Backward compatibility
export const EMPRESA_NAVIGATION = getEmpresaNavigation();
